import { z } from "zod";
import { ZUid, zUid } from "src/types/zUid";

export const bom2aUiSize: "middle" | "small" = "middle";

const zValue = z.string().nullable();
export type ZValue = z.infer<typeof zValue>;
type Bom2aEdField = {
  id: ZUid;
  value: ZValue;
};

export type FilterBom2aColors = {
  query?: string;
};

const zBom2aPosField = z.union([
  z.object({ id: zUid, photo: zValue }),
  z.object({ id: zUid, location: zValue }),
  z.object({ id: zUid, size: zValue }),
  z.object({ id: zUid, descriptionManualValue: zValue }),
  z.object({ id: zUid, composition: zValue }),
  z.object({ id: zUid, comment: zValue }),
]);

export const zBom2aColorField = z.union([
  z.object({ id: zUid, hex: zValue }),
  z.object({ id: zUid, name: zValue }),
  z.object({ id: zUid, article: zValue }),
]);

export const zBom2aColorRow = z.object({
  id: zUid,
  name: zValue,
  article: zValue,
  hex: zValue,
});
export const zBom2aColorsResponse = z.object({
  content: zBom2aColorRow.array(),
  totalElements: z.number(),
});
export type ZBom2aColorsResponse = z.infer<typeof zBom2aColorsResponse>;

export type ZBom2aColorRow = z.infer<typeof zBom2aColorRow>;

type EdBom2aColorFields = {
  hex: Bom2aEdField;
  name: Bom2aEdField;
  article: Bom2aEdField;
};

const zBom2aColorModelField = z.union([
  z.object({ id: zUid, colorReference: zValue }),
  z.object({ id: zUid, comment: zValue }),
  z.object({ id: zUid, colorManual: zValue }),
]);
type ZBom2aColorModelField = z.infer<typeof zBom2aColorModelField>;

type EdBom2aColorModelFields = {
  colorReference: Bom2aEdField;
  comment: Bom2aEdField;
  colorManual: Bom2aEdField;
};
const cvtColorModelFields = (
  src: ZBom2aColorModelField[],
): EdBom2aColorModelFields => ({
  colorReference: findField("colorReference", src, ""),
  comment: findField("comment", src, ""),
  colorManual: findField("colorManual", src, ""),
});

const zBom2aColor = z.object({
  id: zUid.nullable(),
  fields: zBom2aColorField.array(),
});
type ZBom2aColor = z.infer<typeof zBom2aColor>;

export type EdBom2aColor = EdBom2aColorFields & {
  id: ZUid | null;
};
const cvtBom2aColor = ({ id, fields }: ZBom2aColor): EdBom2aColor => ({
  id,
  hex: findField("hex", fields, ""),
  name: findField("name", fields, ""),
  article: findField("article", fields, ""),
});

export const zBom2aPosColorModel = z.object({
  id: zUid.nullable(),
  fields: zBom2aColorModelField.array(),
  color: zBom2aColor,
});
type ZBom2aPosColorModel = z.infer<typeof zBom2aPosColorModel>;

type EdBom2aPosColorModel = {
  id: ZUid | null;
  fields: EdBom2aColorModelFields;
  color: EdBom2aColor;
};

const cvtPosColorModel = (src: ZBom2aPosColorModel): EdBom2aPosColorModel => ({
  id: src.id,
  fields: cvtColorModelFields(src.fields),
  color: cvtBom2aColor(src.color),
});

export const zBom2aColorModel = z.object({
  id: zUid,
  name: z.string(),
  bomPositionColorModel: zBom2aPosColorModel,
});
export type ZBom2aColorModel = z.infer<typeof zBom2aColorModel>;

const zBom2aMaterialCategoryOption = z.object({
  dictId: zUid,
  name: z.string(),
});

const zBom2aMaterialCategory = z.object({
  dictId: zUid.nullable(),
  fieldId: zUid,
  name: z.string().nullable(),
  options: zBom2aMaterialCategoryOption.array(),
});

export const zBom2aPositionRow = z.object({
  id: zUid,
  fields: zBom2aPosField.array(),
  colorModels: zBom2aColorModel.array(),
  materialCategory: zBom2aMaterialCategory,
});
export type ZBom2aPositionRow = z.infer<typeof zBom2aPositionRow>;

export const zBom2aPosition = z.object({
  materialTypeId: zUid,
  materialTypeName: z.string(),
  bomPositionRows: zBom2aPositionRow.array(),
});

export type ZBom2aPosition = z.infer<typeof zBom2aPosition>;

export const zBom2aColorColumn = z.object({
  id: zUid,
  name: z.string(),
});

export type ZBom2aColorColumn = z.infer<typeof zBom2aColorColumn>;

// Костыли, связанные с кривым форматом данных, который приходит в ответе с бэка

export type Bom2aEdFields = {
  photo: Bom2aEdField;
  location: Bom2aEdField;
  size: Bom2aEdField;
  descriptionManualValue: Bom2aEdField;
  composition: Bom2aEdField;
  comment: Bom2aEdField;
};
export type Bom2aEdColorFields = {
  image: ZValue;
  name: ZValue;
  article: ZValue;
};
export type EdBom2aColorModel = Omit<
  ZBom2aColorModel,
  "bomPositionColorModel"
> & {
  bomPositionColorModel: EdBom2aPosColorModel;
};

const cvtColorModel = ({
  bomPositionColorModel,
  ...rest
}: ZBom2aColorModel): EdBom2aColorModel => ({
  bomPositionColorModel: cvtPosColorModel(bomPositionColorModel),
  ...rest,
});

export type Bom2aEdPositionRow = Omit<
  ZBom2aPositionRow,
  "colorModels" | "fields"
> & {
  fields: Bom2aEdFields;
  colorModels: EdBom2aColorModel[];
};
export type Bom2aEdPosition = Omit<ZBom2aPosition, "bomPositionRows"> & {
  bomPositionRows: Bom2aEdPositionRow[];
};

const cvtFieldsList = (list: ZBom2aPositionRow["fields"]): Bom2aEdFields => ({
  photo: findField("photo", list, ""),
  location: findField("location", list, ""),
  size: findField("size", list, ""),
  descriptionManualValue: findField("descriptionManualValue", list, ""),
  composition: findField("composition", list, ""),
  comment: findField("comment", list, ""),
});

const cvtRows = (srcList: ZBom2aPositionRow[]): Bom2aEdPositionRow[] =>
  srcList.map(
    ({ fields, colorModels, ...rest }) =>
      ({
        fields: cvtFieldsList(fields),
        colorModels: colorModels.map(cvtColorModel),
        ...rest,
      }) satisfies Bom2aEdPositionRow,
  );

export const cvtPositions = (list: ZBom2aPosition[]): Bom2aEdPosition[] =>
  list.map(({ bomPositionRows, ...rest }) => ({
    bomPositionRows: cvtRows(bomPositionRows),
    ...rest,
  }));

const findField = <T extends {}>(
  key: string,
  list: T[],
  prefix: string,
): Bom2aEdField => {
  const res = list.find((field) => key in field);
  if (!res)
    throw Error(`Invalid BOM position structire. Expected ${prefix}${key}`);
  return {
    // @ts-ignore
    id: res.id,
    // @ts-ignore
    value: res[key],
  };
};

export const zBom2aMaterialDesc = z.object({
  id: zUid,
  type: z.string(),
  description: z.string(),
  image: z.string().nullish(),
});
export type ZBom2aMaterialDesc = z.infer<typeof zBom2aMaterialDesc>;

export const zBom2aMaterialDescResponse = z.object({
  content: zBom2aMaterialDesc.array(),
  totalElements: z.number(),
});

export type FilterBom2aMaterial = {
  query?: string;
};
